import { useState } from "react";
import {
  displayUnitLabel,
  useUnitDisplay,
} from "../../app/units/UnitDisplayContext";

import { ImportRoleMapper } from "./ImportRoleMapper";
import { NetworkEditor } from "./NetworkEditor";
import "./network.css";

import type { CatalogApi } from "../catalog/api";
import type { NetworkImportApi } from "./api";
import type {
  ProjectDocument,
  ProjectValidationIssue,
  TerrainRefreshResult,
  TerrainTargetPreview,
  TopologyCommand,
} from "./types";
import { guided } from "../../app/guidance/fieldHelpRegistryCore";
import { networkFieldHelp } from "../../app/guidance/fieldHelpRegistry";

export interface NetworkFeatureProps {
  catalog?: Pick<CatalogApi, "listHoses">;
  importApi?: NetworkImportApi;
  initialProject: ProjectDocument;
  onProjectSaved?: (project: ProjectDocument) => Promise<void> | void;
  onManualTerrainRefresh?: (
    project: ProjectDocument,
    values: {
      csvText: string;
      filename: string;
      maximumSpacingM: number;
      surveyed: boolean;
    },
  ) => Promise<TerrainRefreshResult>;
  onTerrainPreview?: (
    project: ProjectDocument,
    maximumSpacingM: number,
  ) => Promise<TerrainTargetPreview>;
  onOpenMeteoTerrainRefresh?: (
    project: ProjectDocument,
    maximumSpacingM: number,
  ) => Promise<TerrainRefreshResult>;
  onTopologyCommand?: (
    command: TopologyCommand,
    project: ProjectDocument,
  ) => Promise<ProjectDocument> | ProjectDocument;
  onUsgsTerrainRefresh?: (
    project: ProjectDocument,
    maximumSpacingM: number,
  ) => Promise<TerrainRefreshResult>;
  validationIssues?: ProjectValidationIssue[];
}

function caughtMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The terrain action failed.";
}
function conciseDisplayNumber(value: number): string {
  return Number(value.toFixed(3)).toString();
}

export function NetworkFeature({
  catalog,
  importApi,
  initialProject,
  onManualTerrainRefresh,
  onOpenMeteoTerrainRefresh,
  onProjectSaved,
  onTerrainPreview,
  onTopologyCommand,
  onUsgsTerrainRefresh,
  validationIssues,
}: NetworkFeatureProps) {
  const [project, setProject] = useState(initialProject);
  const { formatSiValue, toDisplayValue, toSiValue, units } = useUnitDisplay();
  const [maximumSpacingM, setMaximumSpacingM] = useState(30);
  const [preview, setPreview] = useState<TerrainTargetPreview | null>(null);
  const [csvText, setCsvText] = useState("target_id,elevation_ft,note\n");
  const [csvFilename, setCsvFilename] = useState("survey-elevations.csv");
  const [surveyed, setSurveyed] = useState(true);
  const [terrainBusy, setTerrainBusy] = useState(false);
  const [terrainError, setTerrainError] = useState<string | null>(null);
  const [terrainStatus, setTerrainStatus] = useState<string | null>(null);

  const spacingValid =
    Number.isFinite(maximumSpacingM) &&
    maximumSpacingM >= 5 &&
    maximumSpacingM <= 500;

  const previewTerrain = async () => {
    if (onTerrainPreview === undefined || !spacingValid) return;
    setTerrainBusy(true);
    setTerrainError(null);
    try {
      const result = await onTerrainPreview(project, maximumSpacingM);
      setPreview(result);
      setTerrainStatus(
        `${result.target_count} exact targets at up to ${formatSiValue(result.maximum_spacing_m, "head")} spacing.`,
      );
    } catch (caught) {
      setTerrainError(caughtMessage(caught));
    } finally {
      setTerrainBusy(false);
    }
  };

  const applyTerrain = (result: TerrainRefreshResult) => {
    setProject(result.project);
    setPreview(null);
    setTerrainStatus(
      `${result.current_link_count} current, ${result.partial_link_count} partial, ${result.out_of_coverage_link_count} out of coverage, and ${result.failed_link_count} failed link profiles. ${result.provider_warning}`,
    );
  };

  const refreshUsgs = async () => {
    if (onUsgsTerrainRefresh === undefined || !spacingValid) return;
    setTerrainBusy(true);
    setTerrainError(null);
    try {
      applyTerrain(await onUsgsTerrainRefresh(project, maximumSpacingM));
    } catch (caught) {
      setTerrainError(caughtMessage(caught));
    } finally {
      setTerrainBusy(false);
    }
  };

  const refreshOpenMeteo = async () => {
    if (onOpenMeteoTerrainRefresh === undefined || !spacingValid) return;
    setTerrainBusy(true);
    setTerrainError(null);
    try {
      applyTerrain(await onOpenMeteoTerrainRefresh(project, maximumSpacingM));
    } catch (caught) {
      setTerrainError(caughtMessage(caught));
    } finally {
      setTerrainBusy(false);
    }
  };

  const refreshManual = async () => {
    if (
      onManualTerrainRefresh === undefined ||
      !spacingValid ||
      csvFilename.trim() === "" ||
      csvText.trim() === ""
    )
      return;
    setTerrainBusy(true);
    setTerrainError(null);
    try {
      applyTerrain(
        await onManualTerrainRefresh(project, {
          csvText,
          filename: csvFilename,
          maximumSpacingM,
          surveyed,
        }),
      );
    } catch (caught) {
      setTerrainError(caughtMessage(caught));
    } finally {
      setTerrainBusy(false);
    }
  };
  const hasRoute = project.links.length > 0;
  const importWorkspace = (
    <ImportRoleMapper
      {...(catalog === undefined ? {} : { catalog })}
      {...(importApi === undefined ? {} : { api: importApi })}
      onProjectSaved={async (saved) => {
        setProject(saved);
        await onProjectSaved?.(saved);
      }}
      project={project}
    />
  );

  return (
    <div className="network-feature">
      <header className="network-heading">
        <p className="eyebrow">{project.name}</p>
        <h1>Build the network</h1>
        <p>
          {project.nodes.length} nodes · {project.links.length} routes
        </p>
      </header>
      {hasRoute ? (
        <details className="network-workspace-disclosure">
          <summary>Import another route</summary>
          {importWorkspace}
        </details>
      ) : (
        importWorkspace
      )}
      <NetworkEditor
        {...(catalog === undefined ? {} : { catalog })}
        document={project}
        onDocumentChange={setProject}
        {...(onTopologyCommand === undefined
          ? {}
          : { onCommand: onTopologyCommand })}
        {...(validationIssues === undefined ? {} : { validationIssues })}
      />
      {onTerrainPreview === undefined || !hasRoute ? null : (
        <section
          aria-labelledby="terrain-refresh-title"
          className="network-panel"
        >
          <h2 id="terrain-refresh-title">Terrain</h2>
          <p>Project datum: {project.vertical_reference.datum}.</p>
          <details className="network-technical">
            <summary>Terrain source details</summary>
            <p role="note">
              {project.vertical_reference.datum === "NAVD88" ? (
                <>
                  USGS 3DEP EPQS supplies planning-grade contiguous-U.S. NAVD88
                  terrain (about 0.53 m overall RMSE), not survey control.
                </>
              ) : project.vertical_reference.datum === "EGM2008" ? (
                <>
                  The optional licensed Open-Meteo service supplies Copernicus
                  DEM 2021 GLO-90 EGM2008 terrain. It is a 90 m digital surface
                  model that can include buildings and vegetation; reported
                  global absolute vertical accuracy is below 4 m at 90% linear
                  error, not survey control.
                </>
              ) : (
                <>
                  This datum has no compatible automatic provider. Use surveyed
                  or explicit manual CSV elevations in the declared project
                  datum.
                </>
              )}{" "}
              Google Elevation is never used. Long routes can require many point
              requests; preview the count first, increase spacing when
              appropriate, or import surveyed CSV elevations.
            </p>
          </details>
          <div className="network-primary-actions">
            {project.vertical_reference.datum === "NAVD88" &&
            onUsgsTerrainRefresh !== undefined ? (
              <button
                disabled={terrainBusy || !spacingValid}
                onClick={() => void refreshUsgs()}
                type="button"
              >
                Refresh from USGS 3DEP
              </button>
            ) : null}
            {project.vertical_reference.datum === "EGM2008" &&
            onOpenMeteoTerrainRefresh !== undefined ? (
              <button
                disabled={terrainBusy || !spacingValid}
                onClick={() => void refreshOpenMeteo()}
                type="button"
              >
                Refresh from Copernicus GLO-90
              </button>
            ) : null}
          </div>
          <details className="network-toolbox">
            <summary>Terrain settings</summary>
            <fieldset disabled={terrainBusy}>
              <legend>Terrain sampling</legend>
              <label>
                Maximum terrain spacing ({displayUnitLabel(units.head)})
                <input
                  {...guided(networkFieldHelp.terrainSpacing)}
                  aria-label={`Maximum terrain spacing (${displayUnitLabel(units.head)})`}
                  max={toDisplayValue(500, "head")}
                  min={toDisplayValue(5, "head")}
                  onChange={(event) =>
                    setMaximumSpacingM(
                      toSiValue(event.currentTarget.valueAsNumber, "head"),
                    )
                  }
                  step="1"
                  type="number"
                  value={conciseDisplayNumber(
                    toDisplayValue(maximumSpacingM, "head"),
                  )}
                />
              </label>
              <button
                disabled={!spacingValid}
                onClick={() => void previewTerrain()}
                type="button"
              >
                Preview terrain targets
              </button>
            </fieldset>
            {preview === null ? null : (
              <details>
                <summary>
                  {preview.target_count} targets · project datum{" "}
                  {preview.vertical_reference.datum}
                </summary>
                <ul aria-label="Terrain target IDs">
                  {preview.targets.map((target) => (
                    <li key={target.sample_id}>
                      <code>{target.sample_id}</code> — {target.kind} —{" "}
                      {target.location.longitude_deg},{" "}
                      {target.location.latitude_deg}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </details>
          <details
            className="network-toolbox"
            open={
              project.vertical_reference.datum !== "NAVD88" &&
              project.vertical_reference.datum !== "EGM2008"
            }
          >
            <summary>Manual CSV</summary>
            <fieldset disabled={terrainBusy}>
              <legend>Manual terrain</legend>
              <details className="network-technical">
                <summary>CSV format</summary>
                <p>
                  CSV requires <code>target_id</code> and exactly one of
                  <code> elevation_ft</code> or <code>elevation_m</code>; an
                  optional note column is audited. IDs must come from the
                  current target preview. Unlisted targets reuse only exact
                  saved geometry and otherwise remain incomplete.
                </p>
              </details>
              <label>
                Source filename
                <input
                  {...guided(networkFieldHelp.terrainFilename)}
                  aria-label="Terrain CSV source filename"
                  onChange={(event) =>
                    setCsvFilename(event.currentTarget.value)
                  }
                  value={csvFilename}
                />
              </label>
              <label>
                Elevation CSV
                <textarea
                  {...guided(networkFieldHelp.terrainCsv)}
                  aria-label="Terrain elevation CSV"
                  onChange={(event) => setCsvText(event.currentTarget.value)}
                  rows={7}
                  value={csvText}
                />
              </label>
              <label>
                <input
                  {...guided(networkFieldHelp.terrainSurveyed)}
                  checked={surveyed}
                  onChange={(event) => setSurveyed(event.currentTarget.checked)}
                  type="checkbox"
                />
                Treat values as surveyed control (clear for explicit manual
                entry)
              </label>
              <button
                disabled={
                  !spacingValid ||
                  csvFilename.trim() === "" ||
                  csvText.trim() === ""
                }
                onClick={() => void refreshManual()}
                type="button"
              >
                Apply CSV terrain refresh
              </button>
            </fieldset>
          </details>
          {terrainStatus === null ? null : <p role="status">{terrainStatus}</p>}
          {terrainError === null ? null : <p role="alert">{terrainError}</p>}
        </section>
      )}
    </div>
  );
}
